import { useEffect, useId, useMemo, useState } from "react";

import { Save, Cancel } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  postNotificationRule,
  patchNotificationRule,
  deleteNotificationRule,
  NotificationRuleDefinition,
  NotificationRuleModel,
  NotificationRuleOperator,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import {
  DEFAULT_RULE_DEFINITION,
  NUMERIC_OPERATORS,
  OPERATOR_OPTIONS,
  STATUS_OPTIONS,
  describeNotificationRule,
  expandRuleGroupTargets,
  planRuleGroupSave,
  type NotificationRuleGroup,
} from "@/components/online-reporting/notificationRules";

export interface ReportTypeOption {
  value: string;
  label: string;
}

interface NotificationRuleFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  orgId: string;
  reportTypes: ReportTypeOption[];
  defaultPriority: number;
  editingGroup: NotificationRuleGroup | null;
}

interface RuleFormData {
  name: string;
  statuses: string[];
  surveyClasses: string[];
  field: string;
  operator: NotificationRuleOperator;
  value: string;
  valueArray: string[];
  emailAddresses: string[];
  isDefault: boolean;
  priority: number;
}

type RuleFormErrors = Partial<
  Record<"name" | "emails" | "field" | "value", string>
>;

/** A write already sent in the current save attempt, kept so a later failure can be undone. */
type AppliedWrite =
  | { kind: "created"; ruleId: string | undefined }
  | { kind: "updated"; original: NotificationRuleModel };

const FIELD_EXAMPLES = [
  "$.incident_location_address.region_id",
  "$.incident_type",
  "$.property_type",
  "$.value",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Sentinel option that clears a multi-select back to "any". */
const ALL_OPTION = "__all__";

const emptyForm = (priority: number): RuleFormData => ({
  name: "",
  statuses: [],
  surveyClasses: [],
  field: "",
  operator: "equals",
  value: "",
  valueArray: [],
  emailAddresses: [],
  isDefault: false,
  priority,
});

const formFromGroup = (group: NotificationRuleGroup): RuleFormData => {
  const { field, operator, value } = group.ruleDefinition;
  const isArray = Array.isArray(value);
  return {
    name: group.name,
    statuses: group.statuses,
    surveyClasses: group.surveyClasses,
    field: group.isDefault ? "" : field,
    operator: group.isDefault ? "equals" : operator,
    value: group.isDefault || isArray ? "" : String(value ?? ""),
    valueArray: isArray ? (value as string[]).map(String) : [],
    emailAddresses: group.emailAddresses,
    isDefault: group.isDefault,
    priority: group.priority,
  };
};

const labelFor = (options: ReportTypeOption[], value: string): string =>
  options.find((option) => option.value === value)?.label ?? value;

interface MultiSelectFieldProps {
  label: string;
  allLabel: string;
  options: ReportTypeOption[];
  value: string[];
  onChange: (value: string[]) => void;
  helperText: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  allLabel,
  options,
  value,
  onChange,
  helperText,
}) => {
  const labelId = useId();

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        labelId={labelId}
        value={value}
        input={<OutlinedInput notched label={label} />}
        onChange={(event) => {
          const next = event.target.value as string[];
          // Picking "All …" is how the user gets back to the unscoped (null) state.
          onChange(next.includes(ALL_OPTION) ? [] : next);
        }}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <Chip label={allLabel} size="small" color="info" />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((option) => (
                <Chip
                  key={option}
                  label={labelFor(options, option)}
                  size="small"
                />
              ))}
            </Box>
          )
        }
      >
        <MenuItem value={ALL_OPTION}>
          <Checkbox checked={value.length === 0} />
          <ListItemText primary={allLabel} />
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={value.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};

const NotificationRuleFormDialog: React.FC<NotificationRuleFormDialogProps> = ({
  open,
  onClose,
  onSaved,
  orgId,
  reportTypes,
  defaultPriority,
  editingGroup,
}) => {
  const [ruleForm, setRuleForm] = useState<RuleFormData>(
    emptyForm(defaultPriority),
  );
  const [errors, setErrors] = useState<RuleFormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRuleForm(
        editingGroup ? formFromGroup(editingGroup) : emptyForm(defaultPriority),
      );
      setErrors({});
    }
  }, [open, editingGroup, defaultPriority]);

  const reportTypeLabels = useMemo(
    () =>
      Object.fromEntries(reportTypes.map(({ value, label }) => [value, label])),
    [reportTypes],
  );

  const buildRuleDefinition = (): NotificationRuleDefinition => {
    if (ruleForm.isDefault) return DEFAULT_RULE_DEFINITION;
    return {
      field: ruleForm.field,
      operator: ruleForm.operator,
      value:
        ruleForm.operator === "in_list"
          ? ruleForm.valueArray
          : NUMERIC_OPERATORS.includes(ruleForm.operator)
            ? Number(ruleForm.value)
            : ruleForm.value,
    };
  };

  const preview = describeNotificationRule(
    {
      statuses: ruleForm.statuses,
      surveyClasses: ruleForm.surveyClasses,
      emailAddresses: ruleForm.emailAddresses,
      ruleDefinition: buildRuleDefinition(),
      isDefault: ruleForm.isDefault,
    },
    reportTypeLabels,
  );

  const validate = (): RuleFormErrors => {
    const next: RuleFormErrors = {};
    if (!ruleForm.name.trim()) next.name = "Rule name is required";
    if (ruleForm.emailAddresses.length === 0) {
      next.emails = "Add at least one recipient";
    } else {
      const invalid = ruleForm.emailAddresses.find((e) => !EMAIL_RE.test(e));
      if (invalid) next.emails = `"${invalid}" is not a valid email address`;
    }
    if (!ruleForm.isDefault) {
      if (!ruleForm.field.trim()) {
        next.field = "Field path is required for conditional rules";
      }
      if (ruleForm.operator === "in_list") {
        if (ruleForm.valueArray.length === 0)
          next.value = "Add at least one value";
      } else if (!ruleForm.value.trim()) {
        next.value = "Value is required";
      } else if (
        NUMERIC_OPERATORS.includes(ruleForm.operator) &&
        Number.isNaN(Number(ruleForm.value))
      ) {
        next.value = "Value must be a number";
      }
    }
    return next;
  };

  /**
   * Undo a half-applied save. Rows created in this attempt are removed; rows repointed in it are
   * patched back to the snapshot taken before the request went out, so a row that was never
   * actually touched is simply rewritten with the values it already had.
   */
  const rollback = async (writes: AppliedWrite[]): Promise<boolean> => {
    let undone = true;
    try {
      for (const write of [...writes].reverse()) {
        if (write.kind === "created") {
          if (!write.ruleId) {
            // The POST answered without the row it made, so there is no id to delete.
            undone = false;
            continue;
          }
          await deleteNotificationRule({
            path: { org_id: orgId, rule_id: write.ruleId },
          });
        } else {
          const row = write.original;
          await patchNotificationRule({
            path: { org_id: orgId, rule_id: row.id },
            body: {
              name: row.name,
              rule_definition: row.rule_definition,
              email_addresses: row.email_addresses,
              priority: row.priority,
              is_default: row.is_default,
              status: row.status ?? null,
              survey_class: row.survey_class ?? null,
            },
          });
        }
      }
      return undone;
    } catch (error) {
      console.error("Error rolling back notification rule save:", error);
      return false;
    }
  };

  const handleSave = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const shared = {
      name: ruleForm.name.trim(),
      rule_definition: buildRuleDefinition(),
      email_addresses: ruleForm.emailAddresses,
      priority: ruleForm.priority,
      is_default: ruleForm.isDefault,
    };

    // One backing row per (action × report type) — see notificationRules.ts.
    const existing = editingGroup?.rules ?? [];
    const plan = planRuleGroupSave(
      existing,
      expandRuleGroupTargets(ruleForm.statuses, ruleForm.surveyClasses),
    );
    const rowsById = new Map(existing.map((rule) => [rule.id, rule]));
    const applied: AppliedWrite[] = [];

    setSaving(true);
    try {
      // Phase 1 — write the rows the group needs. A group is N rows but N separate requests, so
      // every write is recorded first and undone together if any of them fails.
      try {
        for (const { ruleId, target } of plan.updates) {
          const original = rowsById.get(ruleId);
          if (original) applied.push({ kind: "updated", original });
          await patchNotificationRule({
            path: { org_id: orgId, rule_id: ruleId },
            body: {
              ...shared,
              status: target.status,
              survey_class: target.surveyClass,
            },
          });
        }
        for (const target of plan.creates) {
          const created = await postNotificationRule({
            path: { org_id: orgId },
            body: {
              ...shared,
              status: target.status,
              survey_class: target.surveyClass,
            },
          });
          applied.push({ kind: "created", ruleId: created.data?.id });
        }
      } catch (error) {
        console.error("Error saving notification rule:", error);
        const undone = await rollback(applied);
        onSaved();
        if (undone) {
          // Nothing persisted, so the open dialog still describes the saved state exactly and
          // retrying Save is safe.
          toast.error("Failed to save rule");
        } else {
          // Some of the group did persist. Editing on from here would write against rows that no
          // longer match what is on screen, so send the user back to a refreshed list.
          toast.error(
            "Failed to save rule and could not undo it. The rule list has been refreshed — please check it.",
          );
          onClose();
        }
        return;
      }

      // Phase 2 — drop the rows the group no longer covers. The rule itself is already correct, so
      // a failure here leaves a stale extra rule behind rather than a half-written one.
      try {
        for (const ruleId of plan.deletes) {
          await deleteNotificationRule({
            path: { org_id: orgId, rule_id: ruleId },
          });
        }
      } catch (error) {
        console.error("Error removing replaced notification rules:", error);
        toast.warning(
          "Rule saved, but the rows it replaced could not be removed. The rule list has been refreshed — please check it.",
        );
        onSaved();
        onClose();
        return;
      }

      toast.success(editingGroup ? "Rule updated" : "Rule created");
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editingGroup ? "Edit Rule" : "Add New Rule"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Rule Name"
            value={ruleForm.name}
            onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
            placeholder="e.g., Records - Approved Breach of Release"
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <MultiSelectField
                label="Report Types"
                allLabel="All Report Types"
                options={reportTypes}
                value={ruleForm.surveyClasses}
                onChange={(surveyClasses) =>
                  setRuleForm({ ...ruleForm, surveyClasses })
                }
                helperText="Leave as All Report Types to cover every form."
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <MultiSelectField
                label="Actions"
                allLabel="Any Action"
                options={STATUS_OPTIONS}
                value={ruleForm.statuses}
                onChange={(statuses) => setRuleForm({ ...ruleForm, statuses })}
                helperText="Fires when a report moves into any of these statuses."
              />
            </Box>
          </Box>

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={ruleForm.emailAddresses}
            onChange={(_event, newValue) =>
              setRuleForm({
                ...ruleForm,
                emailAddresses: newValue.map((v) => v.trim()),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email Addresses"
                placeholder="Enter address, press Enter to add"
                error={!!errors.emails}
                helperText={
                  errors.emails ?? "Recipients for this rule (one or more)"
                }
                required
              />
            )}
          />

          {!ruleForm.isDefault && (
            <>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Autocomplete
                  freeSolo
                  options={FIELD_EXAMPLES}
                  value={ruleForm.field}
                  onChange={(_event, newValue) =>
                    setRuleForm({ ...ruleForm, field: newValue || "" })
                  }
                  onInputChange={(_event, newValue) =>
                    setRuleForm({ ...ruleForm, field: newValue })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Field Path"
                      placeholder="e.g., $.incident_location_address.region_id"
                      error={!!errors.field}
                      helperText={
                        errors.field ??
                        "JSONPath expression (RFC 9535). Examples are suggestions only."
                      }
                      required
                    />
                  )}
                  sx={{ flex: 1 }}
                />
                <FormControl sx={{ minWidth: 150 }}>
                  <Select
                    value={ruleForm.operator}
                    onChange={(e) =>
                      setRuleForm({
                        ...ruleForm,
                        operator: e.target.value as NotificationRuleOperator,
                        value: "",
                        valueArray: [],
                      })
                    }
                  >
                    {OPERATOR_OPTIONS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {ruleForm.operator === "in_list" ? (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={ruleForm.valueArray}
                  onChange={(_event, newValue) =>
                    setRuleForm({ ...ruleForm, valueArray: newValue })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Value List"
                      placeholder="Enter values, press Enter to add"
                      error={!!errors.value}
                      helperText={
                        errors.value ??
                        "Field value matches if it equals any of these"
                      }
                    />
                  )}
                />
              ) : (
                <TextField
                  type={
                    NUMERIC_OPERATORS.includes(ruleForm.operator)
                      ? "number"
                      : "text"
                  }
                  label="Value"
                  value={ruleForm.value}
                  onChange={(e) =>
                    setRuleForm({ ...ruleForm, value: e.target.value })
                  }
                  placeholder={
                    NUMERIC_OPERATORS.includes(ruleForm.operator)
                      ? "Enter a number"
                      : "Enter the value to match"
                  }
                  error={!!errors.value}
                  helperText={errors.value}
                  required
                />
              )}
            </>
          )}

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              type="number"
              label="Priority"
              value={ruleForm.priority}
              onChange={(e) =>
                setRuleForm({
                  ...ruleForm,
                  priority: parseInt(e.target.value) || 0,
                })
              }
              inputProps={{ min: 0 }}
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={ruleForm.isDefault}
                  onChange={(e) => {
                    const isDefault = e.target.checked;
                    setRuleForm({
                      ...ruleForm,
                      isDefault,
                      field: isDefault ? "" : ruleForm.field,
                      operator: isDefault ? "equals" : ruleForm.operator,
                      value: isDefault ? "" : ruleForm.value,
                      valueArray: isDefault ? [] : ruleForm.valueArray,
                    });
                  }}
                />
              }
              label="Default Rule"
            />
            {ruleForm.isDefault && (
              <Typography variant="caption" color="text.secondary">
                Default rules fire only when no conditional rule matches for the
                same action and report type.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "info.main",
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              display="block"
            >
              In plain English
            </Typography>
            <Typography variant="body2">{preview}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<Cancel />} onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          startIcon={<Save />}
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          Save Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationRuleFormDialog;
